import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../../lib/queryKeys";
import { Task } from "../../types";
import { useUpdateSubtask } from "./useUpdateSubtask";

/**
 * Returns a `toggleSubtask(id)` that flips a single subtask's completed state.
 * The optimistic cache flip, DB commit, and rollback-on-failure are delegated
 * to {@link useUpdateSubtask} (patching only `completed`); on top of that this
 * hook fires the explicit Google Calendar complete/uncomplete sync that keeps
 * the event on the calendar and toggles the ✅ title marker. The delegated
 * update's own "update" sync is skipped so it can't race the explicit
 * complete/uncomplete patch and overwrite the title back to the bare form.
 *
 * This is the query-cache-aware replacement for the pre-migration toggle.
 */
export function useToggleSubtask() {
  const queryClient = useQueryClient();
  const updateSubtask = useUpdateSubtask();

  return async (subtaskId: string): Promise<boolean> => {
    const tasks = queryClient.getQueryData<Task[]>(queryKeys.tasks) ?? [];
    const task = tasks.find((t) =>
      t.subtasks?.some((st) => st.id === subtaskId),
    );
    const subtask = task?.subtasks?.find((st) => st.id === subtaskId);
    if (!subtask || !task) return false;

    const result = await updateSubtask(subtaskId, {
      completed: !subtask.completed,
    });

    return result;
  };
}
